/*
  Gift checkout guard.

  A gift is made free by a 100%-off automatic discount, and that discount does
  not care what else is in the bag. So nothing stopped a shopper checking out a
  gift on its own, or a gift whose promotion has since ended — the gift engine
  only removes gifts it still finds in a live promotion, and does nothing at all
  when the Gift App embed is switched off. Both have let free gifts through.

  snippets/expand-cart-footer.liquid renders <gift-checkout-guard> when the bag
  holds only gifts, or holds a gift no live promotion awards. In that state
  Checkout is a plain button, so nothing submits even without this script. This
  tells the shopper why and offers the one way forward: remove those gifts.
*/
class GiftCheckoutGuard extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.lineKeys = JSON.parse(this.dataset.lineKeys);
    this.variantIds = JSON.parse(this.dataset.variantIds);

    /* The button is the only exit, same rule as the gift modal.

       preventDefault on `cancel` is not enough on its own. A dialog opened
       without user activation on that page — which is exactly what this one
       does — gets a `cancel` event that is NOT cancelable, so Chrome closes it
       on Escape and preventDefault does nothing. Re-opening on `close` is what
       actually holds. Verified as closable on LEGO - DEV, 2026-09-16. */
    this.dialog.addEventListener('cancel', (event) => event.preventDefault());
    this.dialog.addEventListener('close', () => {
      if (!this.removing) this.open();
    });
    this.querySelector('[data-remove-gifts]').addEventListener('click', (event) =>
      this.removeGifts(event.currentTarget),
    );
    document.getElementById('checkout')?.addEventListener('click', () => this.open());

    /* The engine is a deferred app-embed script, so on a fresh page it may not
       have run yet. readyState is already "interactive" while deferred scripts
       run, so it cannot tell us whether the engine has had its turn — `load` can. */
    if (document.readyState === 'complete') {
      this.openUnlessEngineWillClear();
    } else {
      window.addEventListener('load', () => this.openUnlessEngineWillClear(), { once: true });
    }
  }

  /*
    The engine removes every gift it finds in the promotions list once nothing
    in the bag earns it — removing the last paid item on a live promotion
    renders this straight away, and a moment later the engine clears the gift
    and reloads. Opening now would flash "promotion ended" at a shopper who just
    removed their set, so those are left to the engine. A gift whose promotion
    has ended is not in that list, so the engine leaves it alone and this opens
    by itself. If the engine fails, Checkout still opens the dialog.
  */
  openUnlessEngineWillClear() {
    const engineRunning = typeof window.giftApp?.isOptedOut === 'function';
    const recognised = new Set(
      (window.GiftAppData?.promotions ?? []).map((promotion) => String(promotion.giftVariantId)),
    );
    const engineWillClear = engineRunning && this.variantIds.every((id) => recognised.has(String(id)));

    if (!engineWillClear) this.open();
  }

  open() {
    if (this.isConnected && !this.dialog.open) this.dialog.showModal();
  }

  async removeGifts(button) {
    // Lets the dialog close for good — the reload takes over from here.
    this.removing = true;
    button.disabled = true;
    // By line key: a variant id only clears the first line it matches.
    const updates = Object.fromEntries(this.lineKeys.map((key) => [key, 0]));

    try {
      await fetch(routes.cart_update_url, { ...fetchConfig(), body: JSON.stringify({ updates }) });
    } catch (error) {
      console.warn('[gift-checkout-guard] could not remove gifts', error);
    }

    // Reload either way: a bag still holding a blocked gift renders the guard again.
    window.location.reload();
  }
}

if (!customElements.get('gift-checkout-guard')) {
  customElements.define('gift-checkout-guard', GiftCheckoutGuard);
}
